import * as THREE from 'three';

const _v1 = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _v3 = new THREE.Vector3();
const _local = new THREE.Vector3();
const _dirLocal = new THREE.Vector3();
const _originLocal = new THREE.Vector3();
const _q = new THREE.Quaternion();

let _nextId = 1;

/** A capsule expressed as a segment plus a radius. */
export class Capsule {
  constructor(start = new THREE.Vector3(), end = new THREE.Vector3(0, 1, 0), radius = 0.4) {
    this.start = start.clone();
    this.end = end.clone();
    this.radius = radius;
  }

  setFromFeet(feet, height, radius) {
    this.radius = radius;
    this.start.set(feet.x, feet.y + radius, feet.z);
    this.end.set(feet.x, feet.y + height - radius, feet.z);
    return this;
  }

  translate(v) { this.start.add(v); this.end.add(v); return this; }

  center(out = new THREE.Vector3()) {
    return out.copy(this.start).add(this.end).multiplyScalar(0.5);
  }

  /** Point on the capsule axis closest to p. */
  closestOnAxis(p, out) {
    const ax = this.end.x - this.start.x;
    const ay = this.end.y - this.start.y;
    const az = this.end.z - this.start.z;
    const lenSq = ax * ax + ay * ay + az * az;
    let t = 0;
    if (lenSq > 1e-12) {
      t = ((p.x - this.start.x) * ax + (p.y - this.start.y) * ay + (p.z - this.start.z) * az) / lenSq;
      t = t < 0 ? 0 : t > 1 ? 1 : t;
    }
    return out.set(this.start.x + ax * t, this.start.y + ay * t, this.start.z + az * t);
  }
}

/**
 * Oriented box collider. The whole world is built from these: they are exact
 * for the blocky architecture the game uses, cheap to test against, and their
 * faces give portals a well defined rectangle to be clamped into.
 */
export class BoxCollider {
  constructor({
    position = [0, 0, 0],
    quaternion = null,
    rotation = null,           // euler degrees [x, y, z]
    halfExtents = [1, 1, 1],
    portalable = false,
    tags = [],
    trigger = false,
    userData = {},
  } = {}) {
    this.id = _nextId++;
    this.position = new THREE.Vector3().fromArray(position);
    this.quaternion = new THREE.Quaternion();
    if (quaternion) this.quaternion.fromArray(quaternion);
    else if (rotation) {
      this.quaternion.setFromEuler(new THREE.Euler(
        THREE.MathUtils.degToRad(rotation[0] || 0),
        THREE.MathUtils.degToRad(rotation[1] || 0),
        THREE.MathUtils.degToRad(rotation[2] || 0),
      ));
    }
    this.halfExtents = new THREE.Vector3().fromArray(halfExtents);
    this.portalable = portalable;
    this.trigger = trigger;
    this.tags = new Set(tags);
    this.userData = userData;

    this.enabled = true;
    this.velocity = new THREE.Vector3();   // kinematic movers set this
    this.isKinematic = false;

    this.matrix = new THREE.Matrix4();
    this.inverse = new THREE.Matrix4();
    this.aabb = new THREE.Box3();
    this.boundingRadius = 0;
    this.updateTransform();
  }

  hasTag(t) { return this.tags.has(t); }

  updateTransform() {
    this.matrix.compose(this.position, this.quaternion, _v1.set(1, 1, 1));
    this.inverse.copy(this.matrix).invert();
    this.boundingRadius = this.halfExtents.length();

    // World AABB of the oriented box: project the half extents onto the axes.
    const m = this.matrix.elements;
    const ex = Math.abs(m[0]) * this.halfExtents.x + Math.abs(m[4]) * this.halfExtents.y + Math.abs(m[8]) * this.halfExtents.z;
    const ey = Math.abs(m[1]) * this.halfExtents.x + Math.abs(m[5]) * this.halfExtents.y + Math.abs(m[9]) * this.halfExtents.z;
    const ez = Math.abs(m[2]) * this.halfExtents.x + Math.abs(m[6]) * this.halfExtents.y + Math.abs(m[10]) * this.halfExtents.z;
    this.aabb.min.set(this.position.x - ex, this.position.y - ey, this.position.z - ez);
    this.aabb.max.set(this.position.x + ex, this.position.y + ey, this.position.z + ez);
    return this;
  }

  setPosition(x, y, z) { this.position.set(x, y, z); return this.updateTransform(); }

  toLocal(p, out) { return out.copy(p).applyMatrix4(this.inverse); }
  toWorld(p, out) { return out.copy(p).applyMatrix4(this.matrix); }

  /** World-space axis (0=x,1=y,2=z) of this box. */
  axis(index, out) {
    const m = this.matrix.elements;
    const o = index * 4;
    return out.set(m[o], m[o + 1], m[o + 2]).normalize();
  }

  /** Closest point on (or in) the box to a world point. */
  closestPoint(p, out) {
    this.toLocal(p, _local);
    _local.x = THREE.MathUtils.clamp(_local.x, -this.halfExtents.x, this.halfExtents.x);
    _local.y = THREE.MathUtils.clamp(_local.y, -this.halfExtents.y, this.halfExtents.y);
    _local.z = THREE.MathUtils.clamp(_local.z, -this.halfExtents.z, this.halfExtents.z);
    return this.toWorld(_local, out);
  }

  containsPoint(p, margin = 0) {
    this.toLocal(p, _local);
    return Math.abs(_local.x) <= this.halfExtents.x + margin
        && Math.abs(_local.y) <= this.halfExtents.y + margin
        && Math.abs(_local.z) <= this.halfExtents.z + margin;
  }

  /**
   * Slab intersection in local space.
   * @returns {null | {distance:number, point:THREE.Vector3, normal:THREE.Vector3, axis:number, sign:number}}
   */
  raycast(origin, dir, maxDist, out = {}) {
    _originLocal.copy(origin).applyMatrix4(this.inverse);
    // Direction transforms by rotation only; the inverse matrix has no scale so
    // transformDirection keeps it unit length.
    _dirLocal.copy(dir).transformDirection(this.inverse);

    let tmin = 0;
    let tmax = maxDist;
    let hitAxis = -1;
    let hitSign = 1;

    for (let a = 0; a < 3; a++) {
      const o = a === 0 ? _originLocal.x : a === 1 ? _originLocal.y : _originLocal.z;
      const d = a === 0 ? _dirLocal.x : a === 1 ? _dirLocal.y : _dirLocal.z;
      const h = a === 0 ? this.halfExtents.x : a === 1 ? this.halfExtents.y : this.halfExtents.z;

      if (Math.abs(d) < 1e-8) {
        if (o < -h || o > h) return null;
        continue;
      }
      const inv = 1 / d;
      let t1 = (-h - o) * inv;
      let t2 = (h - o) * inv;
      let sign = -1;
      if (t1 > t2) { const tmp = t1; t1 = t2; t2 = tmp; sign = 1; }
      if (t1 > tmin) { tmin = t1; hitAxis = a; hitSign = sign; }
      if (t2 < tmax) tmax = t2;
      if (tmin > tmax) return null;
    }
    if (hitAxis < 0) return null;   // ray started inside: no useful face

    out.distance = tmin;
    out.axis = hitAxis;
    out.sign = hitSign;
    out.point = (out.point || new THREE.Vector3()).copy(origin).addScaledVector(dir, tmin);
    out.normal = (out.normal || new THREE.Vector3());
    this.axis(hitAxis, out.normal).multiplyScalar(hitSign);
    out.collider = this;
    return out;
  }

  /**
   * Capsule overlap test. Two refinement passes converge well for boxes, which
   * is all the level geometry is made of.
   * @returns {null | {normal:THREE.Vector3, depth:number, point:THREE.Vector3}}
   */
  capsuleContact(capsule, out = {}) {
    // Cheap reject against the world AABB grown by the capsule radius.
    const r = capsule.radius;
    const minX = Math.min(capsule.start.x, capsule.end.x) - r;
    const maxX = Math.max(capsule.start.x, capsule.end.x) + r;
    if (maxX < this.aabb.min.x || minX > this.aabb.max.x) return null;
    const minY = Math.min(capsule.start.y, capsule.end.y) - r;
    const maxY = Math.max(capsule.start.y, capsule.end.y) + r;
    if (maxY < this.aabb.min.y || minY > this.aabb.max.y) return null;
    const minZ = Math.min(capsule.start.z, capsule.end.z) - r;
    const maxZ = Math.max(capsule.start.z, capsule.end.z) + r;
    if (maxZ < this.aabb.min.z || minZ > this.aabb.max.z) return null;

    capsule.closestOnAxis(this.position, _v1);
    this.closestPoint(_v1, _v2);
    capsule.closestOnAxis(_v2, _v1);
    this.closestPoint(_v1, _v2);

    _v3.subVectors(_v1, _v2);
    const distSq = _v3.lengthSq();

    if (distSq > r * r) return null;

    out.point = (out.point || new THREE.Vector3()).copy(_v2);
    out.normal = out.normal || new THREE.Vector3();

    if (distSq > 1e-10) {
      const dist = Math.sqrt(distSq);
      out.normal.copy(_v3).multiplyScalar(1 / dist);
      out.depth = r - dist;
    } else {
      // Axis point is inside the box: escape through the nearest face.
      this.toLocal(_v1, _local);
      const dx = this.halfExtents.x - Math.abs(_local.x);
      const dy = this.halfExtents.y - Math.abs(_local.y);
      const dz = this.halfExtents.z - Math.abs(_local.z);
      let axis = 0, depth = dx, sign = Math.sign(_local.x) || 1;
      if (dy < depth) { axis = 1; depth = dy; sign = Math.sign(_local.y) || 1; }
      if (dz < depth) { axis = 2; depth = dz; sign = Math.sign(_local.z) || 1; }
      this.axis(axis, out.normal).multiplyScalar(sign);
      out.depth = depth + r;
    }
    out.collider = this;
    return out;
  }

  /**
   * The rectangle of one face in local space, as {axis, sign, u, v, halfU, halfV}
   * where u/v are the local axis indices spanning the face.
   */
  faceRect(axis, sign) {
    const u = (axis + 1) % 3;
    const v = (axis + 2) % 3;
    const he = this.halfExtents.toArray();
    return { axis, sign, u, v, halfU: he[u], halfV: he[v], offset: he[axis] * sign };
  }

  /** Build a THREE.Object3D-compatible matrix for rendering a debug box. */
  debugMatrix(out = new THREE.Matrix4()) {
    return out.compose(this.position, this.quaternion, _v1.copy(this.halfExtents).multiplyScalar(2));
  }
}

export { _q as _scratchQuat };
